import type { Session } from '@/shared/auth/session';

/**
 * Persistência da sessão.
 *
 * `sessionStorage` (não `localStorage`): o token é a própria credencial de
 * acesso, então ele morre ao fechar a aba e não fica disponível para outras
 * abas nem sobrevive ao navegador. Isso resolve a perda de sessão a cada F5 do
 * painel antigo sem ampliar a janela de exposição mais do que o necessário.
 *
 * O TTL real é do Redis no back-end (30 min por padrão): mesmo com credencial
 * guardada, um request expirado devolve 401 e a sessão é descartada.
 */

// Prefixo do produto, e não de um cliente: o painel serve qualquer evento
// parametrizado. Trocar esta chave descarta as sessões guardadas — quem estava
// logado volta ao login uma vez, e não há nada a migrar (o token vale 30 min).
const STORAGE_KEY = 'inteegra.approval.session';

// Só a credencial é obrigatória: o nome do operador é enfeite de cabeçalho, e
// sessão guardada antes de ele existir continua valendo.
function isSession(value: unknown): value is Session {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<Session>;
  return typeof candidate.email === 'string' && candidate.email.length > 0 && typeof candidate.token === 'string' && candidate.token.length > 0;
}

export function readStoredSession(): Session | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isSession(parsed) ? parsed : null;
  } catch {
    // Storage indisponível (modo privado restrito) ou JSON corrompido:
    // tratar como "sem sessão" é sempre seguro.
    return null;
  }
}

export function writeStoredSession(session: Session): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Sem persistência a sessão só vale para esta navegação — aceitável.
  }
}

export function clearStoredSession(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nada a fazer.
  }
}
